import { Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from './layouts/Layout';
import { LoginPage } from './pages/LoginPage';
import { DingtalkCallbackPage } from './pages/DingtalkCallbackPage';
import { DashboardPage } from './pages/DashboardPage';
import { MyEvalTasksPage } from './pages/MyEvalTasksPage';
import { EmployeesPage } from './pages/EmployeesPage';
import { BatchesPage } from './pages/BatchesPage';
import { BatchDetailPage } from './pages/BatchDetailPage';
import { EvalTasksPage } from './pages/EvalTasksPage';
import { EvalDispatchPage } from './pages/EvalDispatchPage';
import { EvalRelationsPage } from './pages/EvalRelationsPage';
import { EvalResultsMatrixPage } from './pages/EvalResultsMatrixPage';
import { TemplatesQualityPage } from './pages/TemplatesQualityPage';
import { CompetencyModelsPage } from './pages/CompetencyAdminPage';
import { DataDictionaryPage } from './pages/DataDictionaryPage';
import { CompetencyDispatchPage } from './pages/CompetencyDispatchPage';
import { HpAdminPage } from './pages/HpAdminPage';
import { CompetencyHpUnifiedResultsPage } from './pages/CompetencyHpUnifiedResultsPage';
import { MonthlyPerformancePage } from './pages/MonthlyPerformancePage';
import { NineBoxPage } from './pages/NineBoxPage';
import { PortCutoffPage } from './pages/PortCutoffPage';
import { NominationLeadersPage } from './pages/NominationLeadersPage';
import { NominationsPage } from './pages/NominationsPage';
import { RecognitionLeadersPage } from './pages/RecognitionLeadersPage';
import { ReportsPage } from './pages/ReportsPage';
import { PromotionPlanPage } from './pages/PromotionPlanPage';
import { AuditLogPage } from './pages/AuditLogPage';
import { BillingPage } from './pages/BillingPage';
import { LeaderCompetencyPage } from './pages/LeaderCompetencyPage';
import { LeaderCompFormPage } from './pages/LeaderCompFormPage';
import { MyReviewsPage } from './pages/MyReviewsPage';
import { NominatorPage } from './pages/NominatorPage';
import { RecognitionNominatorPage } from './pages/RecognitionNominatorPage';
import { EvalFormPage } from './pages/EvalFormPage';
import { OverviewPage } from './pages/OverviewPage';
import { StoreProgressPage } from './pages/StoreProgressPage';
import { OverviewVisualPrototype } from './pages/OverviewVisualPrototype';
import { TemplatesQualityVisualPrototype } from './pages/TemplatesQualityVisualPrototype';
import { EvalStickySandboxPage } from './pages/EvalStickySandboxPage';
import { useAuthStore } from './store/auth';
import { RequireCap } from './components/RequireCap';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      {import.meta.env.DEV && (
        <>
          <Route path="/prototype/templates-quality" element={<TemplatesQualityVisualPrototype />} />
          <Route path="/prototype/overview" element={<OverviewVisualPrototype />} />
          {/* 免登录：手机端姓名行吸顶验收 */}
          <Route path="/prototype/eval-sticky" element={<EvalStickySandboxPage />} />
        </>
      )}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dingtalk-callback" element={<DingtalkCallbackPage />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Layout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="/my/reviews" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="my/reviews" element={<MyReviewsPage />} />
        <Route path="my/eval-tasks" element={<MyEvalTasksPage />} />
        <Route path="eval/:id" element={<EvalFormPage />} />

        <Route element={<RequireCap capability="ADMIN_CONSOLE" />}>
          <Route path="overview" element={<OverviewPage />} />
          <Route path="store-progress" element={<StoreProgressPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="batches" element={<BatchesPage />} />
          <Route path="batches/:id" element={<BatchDetailPage />} />
          <Route path="templates/quality" element={<TemplatesQualityPage />} />
          <Route path="data-dictionary" element={<DataDictionaryPage />} />
          <Route path="eval-tasks" element={<EvalTasksPage />} />
          <Route path="eval-dispatch" element={<EvalDispatchPage />} />
          <Route path="eval-relations" element={<EvalRelationsPage />} />
          <Route path="eval-results" element={<EvalResultsMatrixPage />} />
          <Route path="competency/models" element={<CompetencyModelsPage />} />
          <Route path="competency/dispatch" element={<CompetencyDispatchPage />} />
          <Route path="hp" element={<HpAdminPage />} />
          <Route
            path="competency-hp/results"
            element={<CompetencyHpUnifiedResultsPage />}
          />
          <Route path="performance/monthly" element={<MonthlyPerformancePage />} />
          <Route path="performance/port-cutoffs" element={<PortCutoffPage />} />
          <Route path="ninebox" element={<NineBoxPage />} />
          <Route path="nomination-leaders" element={<NominationLeadersPage />} />
          <Route path="nominations" element={<NominationsPage />} />
          <Route path="recognition-leaders" element={<RecognitionLeadersPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="promotion-plan" element={<PromotionPlanPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="audit" element={<AuditLogPage />} />
        </Route>

        <Route element={<RequireCap capability="LEADER_EVAL" />}>
          <Route path="leader/competency" element={<LeaderCompetencyPage />} />
          {/* V1.2 起：胜任力 + 高潜识别评价从 Modal 弹窗改为独立页面（与素质项评价 EvalFormPage 一致）。 */}
          <Route path="leader/competency/:id" element={<LeaderCompFormPage />} />
        </Route>

        <Route element={<RequireCap capability="NOMINATE" />}>
          <Route path="nominator" element={<NominatorPage />} />
        </Route>

        <Route element={<RequireCap capability="RECOGNIZE" />}>
          <Route
            path="recognition-nominator"
            element={<RecognitionNominatorPage />}
          />
        </Route>

        <Route path="competency-items" element={<Navigate to="/data-dictionary?tab=competency-items" replace />} />
        <Route path="eval-roles" element={<Navigate to="/data-dictionary?tab=eval-roles" replace />} />
        <Route path="competency" element={<Navigate to="/competency/models" replace />} />
        {/* 旧的「岗位胜任力 · 评价汇总」已并入合并版，保留 URL 兼容性，自动跳转 */}
        <Route
          path="competency/results"
          element={<Navigate to="/competency-hp/results" replace />}
        />
        {/* V1.2 起高潜识别已合并到胜任力评价表单尾部，老链接 /leader/hp 自动跳转过去 */}
        <Route path="leader/hp" element={<Navigate to="/leader/competency" replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/my/reviews" replace />} />
    </Routes>
  );
}
